import React, { FC, useEffect, useState } from 'react';
import { WatchQueryFetchPolicy } from '@apollo/client';
import { TERRITORY_EXPLORE_POSTS_PAGE_SIZE, TTerritoryExploreAllSectionProps } from './constants';
import { useTerritoryArticleList } from '../TerritoryArticlesSection/hooks/useTerritoryArticleList';
import { ArticlesSortingDropdownKey } from '../TerritoryArticlesSection/constants';
import { useCollapsedSection } from './hooks/useCollapsedSection';
import { TArticleCardProps } from '../ArticleCard/ArticleCard';
import { TerritoryExploreContent } from './TerritoryExploreContent';
import { TerritoryVersionType } from '../../layouts/TerritoryPageLayout/TerritoryPageLayout';
import { useAnalytics } from '../AnalyticsProvider/AnalyticsProvider';

interface TerritoryExploreSectionProps extends TTerritoryExploreAllSectionProps {
  sectionTitle: string;
  sortBy: ArticlesSortingDropdownKey;
  initialPageSize?: number;
  where?: object;
  fetchPolicy?: WatchQueryFetchPolicy;
  isPinningEnabled: boolean;
  canManagePinnedArticles: boolean;
  hideSortingDropdown?: boolean;
  isAuthLoading?: boolean;
  territorySlug: string;
  territoryVersion: TerritoryVersionType;
  hideSeeAllButton?: boolean;
  seeAllUrl?: string;
  isOnExplorePage?: boolean;
}

export const TerritoryExploreSection: FC<TerritoryExploreSectionProps> = ({
  territoryId,
  authenticatedUserId,
  sectionTitle,
  sortBy,
  where,
  fetchPolicy,
  isPinningEnabled,
  canManagePinnedArticles,
  initialPageSize = TERRITORY_EXPLORE_POSTS_PAGE_SIZE,
  hideSortingDropdown = false,
  isAuthLoading,
  territorySlug,
  territoryVersion,
  hideSeeAllButton = false,
  seeAllUrl,
  isOnExplorePage = false,
}) => {
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentSortBy, setCurrentSortBy] = useState<ArticlesSortingDropdownKey>(sortBy);
  const { sendEvent } = useAnalytics();

  const {
    getSortedTerritoryArticles,
    articles,
    articlesCount,
    initialArticlesCount,
    hasMoreArticlesToLoad,
    isLoading,
    onLoadMore,
  } = useTerritoryArticleList({
    territoryId,
    userId: authenticatedUserId as string,
    initialPageSize,
  });

  const { isCollapsedSection, renderedArticles, updateCollapsedSection } = useCollapsedSection(
    articles,
    hasMoreArticlesToLoad,
    initialPageSize,
  );

  const showLoadMoreButton = articlesCount > initialArticlesCount;

  const onLoadMoreWithToggle = async () => {
    sendEvent('territory_posts_load_more_click');
    setIsLoadingMore(true);
    await onLoadMore();
    updateCollapsedSection();
    setIsLoadingMore(false);
  };

  const onSortByChange = (sortBy: ArticlesSortingDropdownKey) => {
    setCurrentSortBy(sortBy);
    getSortedTerritoryArticles({
      variables: {
        skip: 0,
        sortBy,
        take: initialPageSize,
        ...(where && { where }),
      },
      ...(fetchPolicy && { fetchPolicy }),
    });
  };

  useEffect(() => {
    getSortedTerritoryArticles({
      notifyOnNetworkStatusChange: true,
      variables: {
        sortBy,
        userId: authenticatedUserId,
        ...(where && { where }),
      },
      ...(fetchPolicy && { fetchPolicy }),
    });
  }, [authenticatedUserId, sortBy, where, fetchPolicy]);

  if (isOnExplorePage && articles?.length < 6) {
    return null;
  }

  return (
    <div className="mb-12 min-h-[492px]">
      <TerritoryExploreContent
        territoryId={territoryId}
        sortBy={currentSortBy}
        sectionTitle={sectionTitle}
        articles={renderedArticles as TArticleCardProps[]}
        hasMoreArticlesToLoad={hasMoreArticlesToLoad}
        onLoadMoreWithToggle={onLoadMoreWithToggle}
        isCollapsedSection={isCollapsedSection}
        isLoading={isLoading && !isLoadingMore}
        showLoadMoreButton={showLoadMoreButton}
        canManagePinnedArticles={canManagePinnedArticles}
        isPinningEnabled={isPinningEnabled}
        hideSortingDropdown={hideSortingDropdown}
        onSortByChange={onSortByChange}
        isAuthLoading={isAuthLoading}
        territorySlug={territorySlug}
        territoryVersion={territoryVersion}
        hideSeeAllButton={hideSeeAllButton}
        seeAllUrl={seeAllUrl}
      />
    </div>
  );
};
